import { useNavigate, useRouterState } from '@tanstack/react-router';
import { Download, FileText, FileX, File as FileIcon, ListChecks, MessageSquare, Search, Wallet, X } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ComponentProps, type KeyboardEvent, type ReactNode } from 'react';
import {
  CRITERION_IMPORTANCE_LABELS,
  RANKING_FILTER_LABELS,
  RANK_TABS,
  RANK_TAB_LABELS,
  activeRankingFilters,
  hasRankingFilters,
  isUncheckable,
  keywordFilter,
  matchesFirst,
  matchesRankingFilters,
  reasonsForStages,
  remarkLine,
  type IcpItem,
  type IcpRanked,
  type JevAskPart,
  type JevAskResults,
  type JevPersonResult,
  type JobDetail,
  type RankTab,
  type RankingCriterionView,
  type RankingFilters,
  type RankingRow,
  type RankingView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import { ModuleGate } from '../../../components/ModuleGate';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { SearchInput } from '../../../components/Input';
import { Segmented, type SegmentedOption } from '../../../components/Segmented';
import { Tabs, type TabItem } from '../../../components/Tabs';
import { Popover } from '../../../components/Popover';
import { MultiSelect } from '../../../components/Select';
import { Skeleton } from '../../../components/Skeleton';
import { Stars } from '../../../components/Stars';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { useModule } from '../../../lib/modules';
import { EMPTY_SELECTION, isSelected, type SelectionState } from '../../../lib/selection';
import { useTableViews } from '../../../lib/table-views-api';
import { useCanEdit, useIsFieldHidden } from '../../../stores/session';
import { usePipelineOptions } from '../../pipeline/api/pipeline-api';
import { RANKING_PANES, rankingPaneOf, validatePipelineSearch, type PipelineSearch, type RankingPane } from '../../pipeline/pipeline-search';
import { registerCommands } from '../../../shell/command-registry';
import { useRejectWithReason, useReviewMove } from '../../shortlist/api/shortlist-api';
import { ReasonPicker } from '../../sorting/components/ReasonPicker';
import { AddToCvBankButton } from '../../cv-bank/components/AddToCvBankButton';
import { useApplicationRemarks, useRemarkActions, type RemarkDraft } from '../../remarks/api/remarks-api';
import { RemarkCell } from '../../remarks/components/RemarkCell';
import { RemarkComposer } from '../../remarks/components/RemarkComposer';
import remarkStyles from '../../remarks/components/remarks.module.css';
import { useJevAsk, useJevStatus, useRunJevAsk } from '../api/jev-api';
import { useKeywordMatches, useRanking, useRankingKeywords, useKeywordSuggestions } from '../api/ranking-api';
import { useRankingExport } from '../api/ranking-export-api';
import { DEFAULT_SORT, SCORE_SORT, isWaitingForReview, jevOrdered, nextTabWithPeople, criterionTitle, rankedRows, sheetPlaceOf, tabCounts, tabsWithMatches, type RankedListRow, type RankingSort } from '../ranking-view';
import { LINE_RATING_WORDS, hasLineFilters, jevLinesOf, lineFilterHelpOf, lineFilterValueOf, lineFiltersOfSearch, matchesLineFilters, type LineFilters } from '../line-filters';
import { filtersOfSearch, keywordsOfView, rankingFilterKeysFor, searchOfView, searchPatchOf, viewCount, viewStateOf } from '../ranking-views';
import { jevFoundOf, perItemHeaderOf, profileRows, useMovedOrder, withProfileRanks } from '../icp-view';
import { noticesOf } from '../ranking-notices';
import { AskJevPanel } from './AskJevPanel';
import { FilterSummary } from './FilterSummary';
import { IcpCard } from './icp/IcpCard';
import { JEV_BUDGET_ANCHOR, JevBudgetPanel } from './JevBudgetPanel';
import { useJevBudget } from '../api/jev-budget-api';
import { useJevBudgetButton } from '../api/use-jev-budget-gate';
import { CandidateSummary, ItemGlyphs, MatchCell, MovedCell } from './icp/IcpCells';
import { JevFoundCell } from './icp/JevFoundCell';
import { IcpDock } from './icp/IcpDock';
import { IcpFocusBar } from './icp/IcpFocusBar';
import { useIcpCardFold } from './icp/use-icp-fold';
import { useIcpNotesSeen } from './icp/use-icp-notes-seen';
import { useIcpWorkspace, type IcpWorkspace } from './icp/use-icp-workspace';
import icpStyles from '../../icp/components/icp.module.css';
import { JevCell, JevReadingLine } from './JevMatch';
import { KeyHints } from './KeyHints';
import { KeywordPanel, KeywordsButton } from './KeywordPanel';
import { LineFilterButtons } from './LineFilterButtons';
import { LineFilterEmpty } from './LineFilterEmpty';
import { RankingFilterBar } from './RankingFilterBar';
import { RankingNotices } from './RankingNotices';
import { SavedViewsBar } from './SavedViewsBar';
import { MarkGlyph } from './MarkGlyph';
import { WhyThisScore } from './WhyThisScore';
import styles from './ranking.module.css';

// R5 — stage ③ Ranking on JOB-12: RNK-01 (05-screen-inventory Part 5b, §L30). Every live
// candidate arrives at once, so a tab, a sort, a search or a focus is worked out here with
// no request (F23.04). The whole view lives in the address (06-qol §6).

type Layout = 'grid' | 'list';

const LAYOUT_OPTIONS = [
  { value: 'grid' as const, label: 'Grid' },
  { value: 'list' as const, label: 'List' },
];

/**
 * ADR 0062, mockup M2: the order switch beside the list on ICP match — the ideal candidate (the job's main ranking) and the screening
 * criteria's score when that module is on. A job still in its old order has only that. Jev's order is the Jev search tab (D5, ADR 0078).
 */
const ICP_OPTION = { value: 'icp' as const, label: 'Ideal candidate' };
const CRITERIA_OPTION = { value: 'score' as const, label: 'Criteria' };
const OLD_ORDER_OPTION = { value: 'score' as const, label: 'Score (old order)' };

type OrderValue = 'score' | 'jev' | 'icp';
const NO_LINE_FILTERS: LineFilters = {};
const NO_ROW_IDS: readonly string[] = [];
const NO_NOTES: readonly string[] = [];
const NO_SORT: readonly SortSpec[] = [];
function noop(): void {}

/** The address's `rorder` for a choice of the Order switch: the job's own main order is the default and leaves no trace. */
export function orderPatchOf(value: OrderValue, isProfileRanked: boolean): PipelineSearch['rorder'] {
  if (value === 'jev') return 'jev';
  if (value === 'score') return isProfileRanked ? 'score' : undefined;
  return undefined;
}

interface HeadNoteInput {
  readonly isOldOrder: boolean;
  readonly isJevOrder: boolean;
  readonly isCriteriaOrder: boolean;
  readonly isFocusing: boolean;
  /** Jev search with no finished ask yet (workflow review D5): the list keeps the main order until Jev has answered. */
  readonly isWaitingForAsk?: boolean;
}

/** The line at the right of the tabs: which order the # is the place in (mockup M2: "Ordered by the ideal candidate profile"). */
export function headNoteOf({ isOldOrder, isJevOrder, isCriteriaOrder, isFocusing, isWaitingForAsk = false }: HeadNoteInput): string {
  if (isJevOrder) return '# is the place in Jev’s order — the match stays the profile’s';
  if (isWaitingForAsk) return 'Ask Jev who you’re looking for, and the list goes into Jev’s order';
  if (isOldOrder) return 'Ordered by the old screening criteria';
  if (isCriteriaOrder) return isFocusing ? 'Ordered by the focus on the chosen criteria' : 'Ordered by the score from the screening criteria';
  return 'Ordered by the ideal candidate profile';
}

/** What the sheet needs to say where a person stands for the profile, and which reading to fetch their findings from. */
function sheetProfileOf(row: RankingRow, icp: IcpWorkspace, place: number | null): ComponentProps<typeof WhyThisScore>['icp'] {
  const askId = row.profileReading?.askId ?? null;
  return {
    items: icp.items,
    ranked: icp.ranks?.get(row.applicationId),
    penalty: row.knockoutPenalty,
    place,
    ask: askId === null ? null : { id: askId },
  };
}

/** RNK-01 §10: `1`–`4` switch tabs. */
const TAB_KEYS: Readonly<Record<string, RankTab>> = { '1': 'top_fit', '2': 'maybe', '3': 'low_fit', '4': 'needs_a_look' };

interface RankingStageProps {
  readonly detail: JobDetail;
  readonly search: PipelineSearch;
  readonly onSearch: (next: PipelineSearch) => void;
}

export function RankingStage({ detail, search, onSearch }: RankingStageProps): ReactNode {
  const ranking = useRanking(detail.id);
  const view = useSettledView(ranking.data);
  const isJevOn = useModule('jev');
  const pane = rankingPaneOf(search, isJevOn);
  const showPane = useCallback((next: RankingPane): void => onSearch(withRanking(search, panePatchOf(search, next))), [search, onSearch]);
  useRankingPaneCommands(detail, isJevOn, showPane);
  if (ranking.isPending) return <RankingSkeleton />;
  if (ranking.error?.status === 403) return <ForbiddenState resource="the automated ranking" contact="Your admin" isInset />;
  if (ranking.error || !view) return <ServerErrorState onRetry={() => void ranking.refetch()} />;
  const page = <Ranking detail={detail} view={view} search={search} onSearch={onSearch} pane={pane} />;
  // #13: with Ask Jev off there is no Jev search, so no tab row with a single tab in it either; the page is ICP match.
  if (!isJevOn) return page;
  return (
    <Tabs label="Automated ranking" tabs={PANE_TABS} value={pane} onChange={showPane}>
      {page}
    </Tabs>
  );
}

/** Workflow review D5: Automated ranking's two tabs. ICP match ranks by the ideal candidate profile; Jev search is Ask Jev. */
export const RANKING_PANE_LABELS: Readonly<Record<RankingPane, string>> = { icp: 'ICP match', jev: 'Jev search' };
const PANE_TABS: readonly TabItem<RankingPane>[] = [
  { value: 'icp', label: RANKING_PANE_LABELS.icp },
  { value: 'jev', label: RANKING_PANE_LABELS.jev },
];

/**
 * The address for a tab. Jev search is named outright, so clearing its ask never drops back to ICP match. ICP match needs
 * naming only while an older link's ask or Jev order would otherwise open Jev search; Jev's order belongs to Jev search.
 */
export function panePatchOf(search: PipelineSearch, pane: RankingPane): Partial<PipelineSearch> {
  if (pane === 'jev') return { rtab: 'jev' };
  return { rtab: search.rask === undefined ? undefined : 'icp', rorder: search.rorder === 'jev' ? undefined : search.rorder };
}

/** 06-qol §1: while a job's Automated ranking is open, each tab is one palette entry away. */
function useRankingPaneCommands(detail: JobDetail, isJevOn: boolean, showPane: (pane: RankingPane) => void): void {
  const latest = useRef(showPane);
  useEffect(() => {
    latest.current = showPane;
  });
  const title = detail.publicTitle ?? detail.title;
  useEffect(() => {
    if (!isJevOn) return undefined;
    return registerCommands(
      RANKING_PANES.map((pane) => ({
        id: `job.ranking.${detail.id}.${pane}`,
        label: `Show ${RANKING_PANE_LABELS[pane]}`,
        section: 'action' as const,
        context: `Automated ranking — ${title}`,
        permission: 'application' as const,
        icon: pane === 'jev' ? Search : ListChecks,
        keywords: ['automated ranking', 'ranking', 'tab', ...(pane === 'jev' ? ['ask jev'] : ['ideal candidate', 'icp'])],
        run: () => latest.current(pane),
      })),
    );
  }, [detail.id, title, isJevOn]);
}

/**
 * RNK-01 "Criteria changed" (AT-8, CLAUDE.md §5a rule 2): while a re-rank runs, the last
 * finished order stays on screen under "Re-ranking…", and the new one replaces it in one go.
 */
function useSettledView(latest: RankingView | undefined): RankingView | undefined {
  const [settled, setSettled] = useState<RankingView | undefined>(undefined);
  // React's "storing information from previous renders": set while rendering, never in an effect.
  if (latest && !latest.isReranking && latest !== settled) setSettled(latest);
  return latest?.isReranking && settled && settled.jobId === latest.jobId ? { ...settled, isReranking: true } : latest;
}

/**
 * ADR 0050 (mockup: "the list stays as it is until the new order is ready"): the last finished ask orders the list
 * while the next one reads; a stopped ask leaves the last finished order in place and says why in the panel.
 */
function useSettledJev(latest: JevAskResults | undefined): JevAskResults | undefined {
  const [settled, setSettled] = useState<JevAskResults | undefined>(undefined);
  if (latest && latest.ask.status === 'done' && latest !== settled) setSettled(latest);
  if (!latest) return undefined;
  if (latest.ask.status === 'done') return latest;
  return settled && settled.ask.jobId === latest.ask.jobId ? settled : undefined;
}

function Ranking({ detail, view, search, onSearch, pane }: { readonly detail: JobDetail; readonly view: RankingView; readonly pane: RankingPane } & Omit<RankingStageProps, 'detail'>): ReactNode {
  // Workflow review D5: ICP match orders by the ideal candidate (or the criteria lens); Jev search is Ask Jev and its order.
  // Everything else — saved views, filters, line filters, keywords, the export, the budget, the table — is the same on both.
  const isJevPane = pane === 'jev';
  const navigate = useNavigate();
  const here = useRouterState({ select: (state) => state.location.href });
  // A Jev budget notification lands here with `#jev-budget`: the panel scrolls into view and is picked out (ADR 0070 stage 3).
  const isBudgetLinked = useRouterState({ select: (state) => state.location.hash.replace(/^#/, '') === JEV_BUDGET_ANCHOR });
  const focusId = useId();
  const tab = search.tab ?? 'top_fit';
  const layout: Layout = search.rlayout === 'list' ? 'list' : 'grid';
  const sort = sortOf(search.rsort);
  const isCriteriaOn = useModule('screening_criteria');
  const isProfileRanked = view.rankedBy === 'profile';
  // The criteria's focus belongs to the criteria order (and the lens); with the module off a saved link's focus is ignored (ADR 0062 §3).
  const focusKeys = useMemo(() => (isCriteriaOn && search.focus ? search.focus.split(',').filter((key) => view.criteria.some((criterion) => criterion.key === key)) : []), [isCriteriaOn, search.focus, view.criteria]);
  // F23.09 (ADR 0048): keywords narrow the list and change no score, weight or tab. A keyword still
  // loading narrows nothing yet; a CV that can't be searched stays in view, flagged (Q2).
  const canEditProfile = useCanEdit('application');
  const canSeeSalary = !useIsFieldHidden('candidate.expectedSalary');
  // ADR 0062: the profile is every job's main ranking. Its ranks are worked out here from the ratings on screen, so a changed rating
  // re-orders the table, moves people between tabs and moves the counts at once, with no request and no new Jev search.
  const icp = useIcpWorkspace({ detail, rows: view.rows, rankedBy: view.rankedBy, isOn: true, onOrder: (isOn) => setIcpOrder(isOn) });
  const baseRows = useMemo(() => (isProfileRanked && icp.ranks ? withProfileRanks(view.rows, icp.ranks) : view.rows), [isProfileRanked, icp.ranks, view.rows]);
  const { keywords, setKeywords } = useRankingKeywords(detail.id);
  // F23.10: the job's saved views, whose counts need their own keywords looked up too (each cached on its own).
  const savedViews = useTableViews('ranking', true, detail.id);
  const allWords = useMemo(() => uniqueWords([...keywords, ...(savedViews.data ?? []).flatMap((saved) => keywordsOfView(saved.state.filters))]), [keywords, savedViews.data]);
  const allMatches = useKeywordMatches(detail.id, allWords);
  const matchSets = useMemo(() => matchSetsOf(allWords, allMatches), [allWords, allMatches]);
  const keywordMatches = useMemo(() => keywords.map((word) => matchSets.get(word.toLowerCase())), [keywords, matchSets]);
  const isNarrowing = keywords.length > 0;
  const narrowed = useMemo(() => keywordFilter(baseRows, keywordMatches.filter((set) => set !== undefined)), [baseRows, keywordMatches]);
  // F23.11: filters narrow after keywords; scores, weights and tabs never move (ADR 0049). Salary (Group 3 Q3) only
  // for someone who may see expected salaries.
  const filterKeys = useMemo(() => rankingFilterKeysFor(canSeeSalary), [canSeeSalary]);
  const filters = useMemo(() => filtersOfSearch(search, filterKeys), [search, filterKeys]);
  // ADR 0068: a filter for each profile line Jev reads, offered once Jev has read someone, with the rating the line has in the job. Like the
  // others it narrows the list and moves no score, order or tab; someone Jev has not read is out while one is on.
  const lineItems = useMemo(() => jevLinesOf(icp.items), [icp.items]);
  const areLineFiltersOffered = isProfileRanked && icp.ranks !== undefined && lineItems.length > 0 && isJevRead(icp.ranks);
  const lineFilters = useMemo(() => (areLineFiltersOffered ? lineFiltersOfSearch(search.rline, icp.items) : NO_LINE_FILTERS), [areLineFiltersOffered, search.rline, icp.items]);
  const isLineFiltering = hasLineFilters(lineFilters);
  const isFiltering = hasRankingFilters(filters) || isLineFiltering;
  const lineSummary = useMemo(() => lineItems.flatMap((item) => (lineFilters[item.id] === undefined ? [] : [`${item.text}: ${lineFilters[item.id] ?? 1} · ${LINE_RATING_WORDS[lineFilters[item.id] ?? 1]}`])), [lineItems, lineFilters]);
  const lineMatches = useCallback((row: RankingRow): boolean => matchesLineFilters(icp.ranks?.get(row.applicationId), icp.items, lineFilters), [icp.ranks, icp.items, lineFilters]);
  const filtered = useMemo(() => (isFiltering ? narrowed.filter((row) => matchesRankingFilters(row, filters) && lineMatches(row)) : narrowed), [narrowed, filters, isFiltering, lineMatches]);
  const counts = useMemo(() => tabCounts(filtered), [filtered]);
  // ADR 0068: when the line filters leave nobody in any tab, say why and which lower ratings would bring people back. Counted the same way
  // as "Showing n" (the people the other filters let through), so pressing a suggestion gives exactly the number it promised.
  const lineHelp = useMemo(
    () => (isLineFiltering && filtered.length === 0 ? lineFilterHelpOf(narrowed.filter((row) => matchesRankingFilters(row, filters)), icp.ranks, icp.items, lineFilters) : null),
    [isLineFiltering, filtered.length, narrowed, filters, icp.ranks, icp.items, lineFilters],
  );
  // ADR 0050: Jev's ask on screen re-orders the tab, once every CV is read; scores, stars and tabs never move.
  // ADR 0050: an Ask Jev search on screen re-orders the tab once every CV is read; scores, stars and tabs never move. Only Ask Jev is behind `jev`.
  const isJevOn = useModule('jev');
  const jevStatus = useJevStatus(isJevOn);
  const askId = isJevOn && isJevPane ? search.rask : undefined;
  const jevAsk = useJevAsk(detail.id, askId);
  const latestAsk = askId !== undefined ? jevAsk.data : undefined;
  const settledJev = useSettledJev(latestAsk);
  const jevResults = useMemo(() => new Map((settledJev?.people ?? []).map((person) => [person.applicationId, person])), [settledJev]);
  const icpFold = useIcpCardFold(detail.id);
  const notesSeen = useIcpNotesSeen(detail.id);
  // Which order is on screen: Jev's (an Ask Jev finished), the criteria's (the lens, or the whole ranking of a job not yet moved), or the profile's.
  const isJevOrder = isJevPane && settledJev !== undefined;
  const isCriteriaOrder = !isProfileRanked || (!isJevPane && search.rorder === 'score' && isCriteriaOn);
  const isProfileOrder = isProfileRanked && !isJevOrder && !isCriteriaOrder;
  const isOldOrder = !isProfileRanked;
  const rows = useMemo(() => {
    const query = search.q ?? '';
    const listed = isProfileOrder
      ? profileRows(filtered, { tab, query, direction: sort.by === SCORE_SORT ? sort.direction : 'desc' })
      : rankedRows(filtered, view.criteria, { tab: isOldOrder ? 'all' : tab, focus: new Set(focusKeys), sort, query });
    const keywordFirst = isNarrowing ? matchesFirst(listed) : listed;
    return isJevOrder ? jevOrdered(keywordFirst, jevResults) : keywordFirst;
  }, [filtered, view.criteria, tab, focusKeys, sort, search.q, isNarrowing, isJevOrder, jevResults, isProfileOrder, isOldOrder]);
  const profileRowIds = useMemo(() => (isProfileOrder ? rows.map((row) => row.applicationId) : NO_ROW_IDS), [isProfileOrder, rows]);
  const profileMoved = useMovedOrder(icp.ratingsKey, profileRowIds);
  /** What the filter counts are taken from: the open tab after keywords and the name search. */
  const filterBase = useMemo(() => {
    const query = (search.q ?? '').trim().toLowerCase();
    return narrowed.filter((row) => row.tab === tab && (query === '' || row.name.toLowerCase().includes(query)) && lineMatches(row));
  }, [narrowed, tab, search.q, lineMatches]);
  const tabTotal = useMemo(() => baseRows.filter((row) => row.tab === tab).length, [baseRows, tab]);
  const uncheckedInTab = isNarrowing ? rows.filter(isUncheckable).length : 0;
  const isKeywordLoading = keywords.some((word) => !matchSets.has(word.toLowerCase()));
  const pending = view.rows.filter((row) => row.isPending).length;
  const ranked = view.rows.length - pending;
  const unfilteredCounts = useMemo(() => tabCounts(baseRows), [baseRows]);
  const waitingCount = useMemo(() => (icp.ranks ? [...icp.ranks.values()].filter((rank) => rank.state === 'waiting').length : 0), [icp.ranks]);
  const jevBudget = useJevBudget(detail.id).data;
  const isBudgetUsedUp = jevBudget?.budgetMicroUsd !== undefined && jevBudget.budgetMicroUsd !== null && jevBudget.spentMicroUsd >= jevBudget.budgetMicroUsd;
  const rankBudget = useJevBudgetButton(detail.id, icp.rankMicroUsd, { isPriced: true });
  // Owner 2026-11-01: the budget stays out of sight. Its button is there on Jev search, when a notification links to it, or
  // when the budget is what stops ranking; it opens the panel in a pop-up.
  const isBudgetOffered = isJevPane || isBudgetLinked || rankBudget.blockedNote !== null;
  const notices = noticesOf({
    isBudgetUsedUp,
    rankedBy: view.rankedBy,
    hasProfile: icp.hasProfile,
    readsCvs: icp.readsCvs,
    isJevConnected: icp.isJevConnected,
    rowCount: view.rows.length,
    needsALookCount: unfilteredCounts.needs_a_look,
    waitingCount,
    readingNew: icp.readingNew,
    reading: icp.reading,
    autoPausedReason: icp.autoPausedReason,
    rankEstimate: icp.rankEstimate,
  });
  const isJobClosed = detail.status === 'filled' || detail.status === 'closed_partial' || detail.status === 'cancelled';
  // R6 (DECISIONS-LOG "Start R5" Q3): "Start review", HR ★ and "select → Shortlist / Reject".
  const canDecide = useCanEdit('screening_decision');
  const isPoolsOn = useModule('pools');
  const mayPool = useCanEdit('talent_pool');
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [rejecting, setRejecting] = useState<{ readonly rows: readonly RankingRow[]; readonly anchor: AnchorRect } | null>(null);
  const { move, pending: movingIds } = useReviewMove(detail.id);
  const rejection = useRejectWithReason(detail.id);
  const options = usePipelineOptions(canDecide);
  const reasons = useMemo(() => reasonsForStages(options.data?.reasons ?? [], ['screening']), [options.data?.reasons]);
  const canStartReview = canDecide && detail.status !== 'draft' && baseRows.some(isWaitingForReview);
  const selected = rows.filter((row) => isSelected(selection, row.applicationId));
  // ADR 0069: the whole job or the ticked people as an Excel file, made on the server.
  const exporter = useRankingExport(detail.id);
  // F23.13: remarks, anchored to the cell (or the bulk button) that opened them.
  const canRemark = useCanEdit('activity_message');
  const remarkActions = useRemarkActions(detail.id);
  /** One remark on many selected rows: a pop-over by the bulk button. */
  const [remarking, setRemarking] = useState<{ readonly rows: readonly RankingRow[]; readonly anchor: AnchorRect } | null>(null);
  /** One row's remark box, opened inside the list under the row (DECISIONS-LOG 2026-10-17 "match the mockup" Q2). */
  const [remarkRowId, setRemarkRowId] = useState<string | null>(null);
  const [saveAnchor, setSaveAnchor] = useState<AnchorRect | null>(null);
  const [budgetAnchor, setBudgetAnchor] = useState<AnchorRect | null>(null);
  const budgetHolder = useRef<HTMLSpanElement | null>(null);
  // A Jev budget notification lands here with `#jev-budget` (ADR 0070 stage 3): the pop-up opens by itself.
  useEffect(() => {
    if (isBudgetLinked && budgetHolder.current) setBudgetAnchor(anchorFrom(budgetHolder.current));
  }, [isBudgetLinked]);

  // On Jev search the tab is named in every change, so clearing the ask or the filters never drops back to ICP match.
  const update = (patch: Partial<PipelineSearch>): void => onSearch(withRanking(search, isJevPane ? { rtab: 'jev', ...patch } : patch));
  // The ask's id arrives after the click; by then the person may have changed tab, so it lands on the latest address.
  const latestSearch = useRef(search);
  useEffect(() => {
    latestSearch.current = search;
  }, [search]);
  // A finished ask orders Jev search; Jev's order is the tab itself now, so `rorder=jev` is no longer written.
  const jevRun = useRunJevAsk(detail.id, (ask) => {
    const here = latestSearch.current;
    onSearch(withRanking(here, { rask: ask.id, rtab: 'jev', rorder: here.rorder === 'jev' ? undefined : here.rorder }));
  });
  /** "Save and rank" brings the order back to the profile's (the default) on ICP match; a refused save leaves the order alone. */
  function setIcpOrder(isOn: boolean): void {
    const here = latestSearch.current;
    if (!isOn || (here.rorder !== 'jev' && here.rorder !== 'score')) return;
    onSearch(withRanking(here, { ...panePatchOf(here, 'icp'), rorder: undefined }));
  }
  /** `i`: edit the ideal candidate on ICP match, or go and set it in job setup when the job has none (people who may edit it only). */
  const openIcp = (): void => {
    if (!canEditProfile) return;
    if (!icp.hasProfile) {
      void navigate({ to: '/jobs/$jobId/setup/$section', params: { jobId: detail.id, section: 'profile' } });
      return;
    }
    if (isJevPane) onSearch(withRanking(latestSearch.current, panePatchOf(latestSearch.current, 'icp')));
    icp.edit();
  };
  const latestOpenIcp = useRef(openIcp);
  useEffect(() => {
    latestOpenIcp.current = openIcp;
  });
  const suggestions = useKeywordSuggestions(detail.id);
  const isJevPickingFilters = isJevOn && (suggestions.data?.suggestions ?? []).some((suggestion) => suggestion.isJevPicked === true);
  const isJevReading = jevRun.isStarting || latestAsk?.ask.status === 'running';
  const shortlistOne = (row: RankingRow): void => void move([{ applicationId: row.applicationId, name: row.name, from: row.reviewStatus ?? 'to_review' }], 'shortlisted');
  const openRemark = (row: RankingRow): void => setRemarkRowId((current) => (current === row.applicationId ? null : row.applicationId));

  /** F23.14 (ADR 0049): one-letter actions on the focused row; R stays reject, as in Review. */
  const onRowKey = (row: RankedListRow, key: string, rowElement: HTMLElement | null): boolean => {
    if (key === 'm' && canRemark) {
      setRemarkRowId(row.applicationId);
      return true;
    }
    if (!canDecide) return false;
    if (key === 'a' && isWaitingForReview(row)) {
      shortlistOne(row);
      return true;
    }
    if (key === 'h' && isWaitingForReview(row) && row.reviewStatus !== 'maybe') {
      void move([{ applicationId: row.applicationId, name: row.name, from: row.reviewStatus ?? 'to_review' }], 'maybe');
      return true;
    }
    if (key === 'r' && row.stage === 'screening' && rowElement) {
      setRejecting({ rows: [row], anchor: anchorFrom(rowElement) });
      return true;
    }
    return false;
  };
  const openedRow = search.app ? baseRows.find((row) => row.applicationId === search.app) : undefined;
  // Redesign Group 3: the sheet steps through the rows on screen, in the order shown (tab, keywords, filters, focus, Jev).
  const place = sheetPlaceOf(rows, search.app);
  const steps = place && {
    position: place.position,
    total: place.total,
    onPrevious: place.previousId === null ? null : () => update({ app: place.previousId ?? undefined }),
    onNext: place.nextId === null ? null : () => update({ app: place.nextId ?? undefined }),
  };
  // Search looks in the open tab; say where else the name is, so "Maybe has 474" is never the only hint (#30).
  const otherMatches = useMemo(() => tabsWithMatches(narrowed, search.q ?? '', tab), [narrowed, search.q, tab]);

  // RNK-01 §10: 1–4 tabs, f focus, g grid/list, s starts review (R6; the button is the job header's).
  // `/` (search), ↑/↓ and Enter are the shell's and the table's.
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.ctrlKey || event.metaKey || event.altKey || search.app || isTyping(event.target)) return;
      const nextTab = TAB_KEYS[event.key];
      if (nextTab) {
        event.preventDefault();
        onSearch(withRanking(search, { tab: nextTab }));
      } else if (event.key === 'f' && isCriteriaOn && view.criteria.length > 1) {
        event.preventDefault();
        document.getElementById(focusId)?.click();
      } else if (event.key === 'i' && canEditProfile) {
        event.preventDefault();
        latestOpenIcp.current();
      } else if (event.key === 'g') {
        event.preventDefault();
        onSearch(withRanking(search, { rlayout: layout === 'grid' ? 'list' : undefined }));
      } else if (event.key === 's' && canStartReview) {
        event.preventDefault();
        // Exit on the review card comes back here, with this page's address (Redesign Group 3).
        void navigate({ to: '/jobs/$jobId/sort', params: { jobId: detail.id }, state: { reviewFrom: here } });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [search, onSearch, focusId, layout, view.criteria.length, canStartReview, navigate, detail.id, here, isCriteriaOn, canEditProfile]);

  // A job still in its old order has its own ranking until it is moved, so only a job with nothing to rank by sends people to setup.
  if (!icp.isLoading && !icp.hasProfile && !icp.error && (isProfileRanked || view.criteria.length === 0)) {
    return (
      <>
        <NoProfile
          detail={detail}
          rows={view.rows}
          canEdit={canEditProfile}
          isCriteriaPromptShown={isCriteriaOn && !isProfileRanked && view.criteria.length === 0}
          onOpenRow={(applicationId) => update({ app: applicationId })}
        />
        <JevBudgetPanel jobId={detail.id} isLinkedTo={isBudgetLinked} />
      </>
    );
  }
  if (view.rows.length === 0) {
    return (
      <>
        <EmptyState
          title="No candidates yet"
          body="Import a spreadsheet of applicants and each one is ranked as it arrives."
          action={
            <Button variant="solid" onClick={() => void navigate({ to: '/jobs/$jobId/$tab', params: { jobId: detail.id, tab: 'import' } })}>
              Import a CSV
            </Button>
          }
          isInset
        />
        <JevBudgetPanel jobId={detail.id} isLinkedTo={isBudgetLinked} />
      </>
    );
  }

  const columns = columnsOf({
    jobId: detail.id,
    criteria: view.criteria,
    layout,
    isFocusing: focusKeys.length > 0,
    open: (applicationId) => update({ app: applicationId }),
    action: canDecide ? { shortlist: shortlistOne, moving: movingIds } : null,
    isNarrowing,
    remark: { canAdd: canRemark, pending: remarkActions.pending, open: openRemark },
    jev: isJevPane && settledJev ? { parts: settledJev.ask.parts, results: jevResults } : null,
    profile: isProfileRanked && icp.ranks ? { items: icp.items, ranks: icp.ranks, moved: profileMoved, isBudgetUsedUp } : null,
    isCriteriaOn,
  });
  const next = nextTabWithPeople(counts, tab);
  const openedPlace = (isJevOrder || isProfileOrder) && openedRow ? (rows.find((row) => row.applicationId === openedRow.applicationId)?.rank ?? null) : null;
  // ICP match's order switch: the ideal candidate, and the criteria lens with that module on. Jev's order is the Jev search tab.
  const orderOptions: readonly SegmentedOption<OrderValue>[] = isOldOrder ? [OLD_ORDER_OPTION] : [ICP_OPTION, ...(isCriteriaOn ? [CRITERIA_OPTION] : [])];
  const orderValue: OrderValue = isCriteriaOrder ? 'score' : 'icp';
  const clearJev = { rask: undefined, rorder: undefined };
  const clearAllFilters = (): void => {
    setKeywords([]);
    update({ ...searchPatchOf({}), rline: undefined, ...clearJev });
  };
  const withoutLine = (id: string): LineFilters => Object.fromEntries(Object.entries(lineFilters).filter(([key]) => key !== id));
  const keywordNote = !isNarrowing
    ? null
    : isKeywordLoading
      ? 'Looking through the CVs…'
      : `${(rows.length - uncheckedInTab).toLocaleString('en-US')} of ${tabTotal.toLocaleString('en-US')} in ${RANK_TAB_LABELS[tab]} have ${keywords.length === 1 ? 'this word' : `all ${keywords.length} words`}.${uncheckedInTab > 0 ? ` ${uncheckedInTab} more couldn't be checked — their CV can't be read, so they stay for you to check.` : ''}`;

  // Workflow review D5 (ADR 0078, replacing design A): the card is ICP match's own; Jev search has the Ask Jev box instead.
  const profileCard =
    (icp.isLoading || icp.error || icp.hasProfile) ? (
      <IcpCard
        state={icp.isLoading ? { kind: 'loading' } : icp.error ? { kind: 'error', onRetry: icp.retry } : { kind: 'built' }}
        items={icp.items}
        staleIds={icp.staleIds}
        isAtBaseline={icp.isAtBaseline}
        canEdit={canEditProfile}
        isEditing={icp.panel === 'form'}
        reading={icp.reading}
        askProblem={icp.askProblem}
        rankedBy={icp.rankedBy}
        readsCvs={icp.readsCvs}
        isJevConnected={icp.isJevConnected}
        readingNew={icp.readingNew}
        lastRead={icp.lastRead}
        spentMicroUsd={icp.spentMicroUsd}
        autoPausedReason={icp.autoPausedReason}
        isJobClosed={isJobClosed}
        migrationNotes={notesSeen.isSeen ? NO_NOTES : icp.migrationNotes}
        onNotesSeen={notesSeen.markSeen}
        flash={icp.flash}
        isRanking={icp.isRanking}
        isFolded={icpFold.isFolded}
        onFoldedChange={icpFold.setFolded}
        onRate={icp.rate}
        onReset={icp.reset}
        onEdit={icp.edit}
        onRank={icp.rank}
        rankBudget={{ suffix: rankBudget.suffix, blockedNote: rankBudget.blockedNote }}
      />
    ) : null;

  return (
    <div className={icpStyles.dock} data-docked={icp.panel !== null || undefined}>
      <div className={icpStyles.dockTop}>
        <SavedViewsBar
          jobId={detail.id}
          current={viewStateOf(search, keywords)}
          appliedViewId={search.view}
          summary={viewSummary(view.criteria, tab, keywords, search, filters, settledJev?.ask.text ?? latestAsk?.ask.text ?? null, isCriteriaOrder && isProfileRanked, lineSummary)}
          countOf={(state) => viewCount(baseRows, state, matchSets, filterKeys, areLineFiltersOffered ? { items: icp.items, ranks: icp.ranks } : undefined)}
          onApply={(saved) => {
            onSearch(searchOfView(search, saved.state.filters, saved.id));
            setKeywords(keywordsOfView(saved.state.filters));
          }}
          onSaved={(saved) => update({ view: saved.id })}
          saveAnchor={saveAnchor}
          onSaveOpen={setSaveAnchor}
          onSaveClose={() => setSaveAnchor(null)}
        />

        <RankingNotices notices={notices} canRank={canEditProfile && rankBudget.blockedNote === null} isRanking={icp.isRanking} onRank={icp.rank} />

        {isJevPane ? (
          <AskJevPanel
            jobId={detail.id}
            provider={jevStatus.data?.provider}
            rows={view.rows}
            criteriaLabels={view.criteria.map((criterion) => criterion.label)}
            current={latestAsk}
            isStarting={jevRun.isStarting}
            startingInput={jevRun.lastInput}
            onRun={jevRun.run}
            onShowUnreadable={() => update(searchPatchOf({ ...filters, rcv: ['unreadable', 'none'] }))}
            onSaveView={(event) => setSaveAnchor(anchorFrom(event.currentTarget))}
            isJevPickingFilters={isJevPickingFilters}
            hotFilters={<KeywordPanel jobId={detail.id} keywords={keywords} onChange={setKeywords} matchCount={(word) => matchSets.get(word.toLowerCase())?.size ?? null} note={keywordNote} isInJevPanel />}
          />
        ) : (
          profileCard
        )}

        {/* ADR 0070: what is left of the job's Jev budget; the same spend figure the Ideal candidate card shows. Owner 2026-11-01:
            a pop-up from the "Jev budget" button beside Export, so the table stays in view. */}
        <Popover anchor={budgetAnchor} onClose={() => setBudgetAnchor(null)} label="Jev budget" align="right">
          <div className={styles.budgetPop}>
            <JevBudgetPanel jobId={detail.id} spentMicroUsd={icp.spentMicroUsd} />
          </div>
        </Popover>
      </div>

      {icp.panel !== null && (
        <div className={icpStyles.dockPanel}>
          <IcpDock detail={detail} workspace={icp} />
        </div>
      )}

      <div className={icpStyles.dockRest}>

        <FilterSummary
          shown={filtered.length}
          total={view.rows.length}
          keywords={keywords}
          filters={filters}
          onRemoveKeyword={(word) => setKeywords(keywords.filter((existing) => existing !== word))}
          onRemoveFilter={(key, value) => update(searchPatchOf({ ...filters, [key]: (filters[key] ?? []).filter((chosen) => chosen !== value) }))}
          lineFilters={lineItems.flatMap((item) => (lineFilters[item.id] === undefined ? [] : [{ id: item.id, name: item.text, rating: lineFilters[item.id] ?? 1, words: LINE_RATING_WORDS[lineFilters[item.id] ?? 1] }]))}
          onRemoveLineFilter={(id) => update({ rline: lineFilterValueOf(withoutLine(id), icp.items) })}
          onClearAll={clearAllFilters}
          onSaveView={(event) => setSaveAnchor(anchorFrom(event.currentTarget))}
          trailing={
            <>
              {isBudgetOffered && (
                <span ref={budgetHolder}>
                  <Button size="sm" variant="outline" icon={Wallet} onClick={(event) => setBudgetAnchor(anchorFrom(event.currentTarget))}>
                    Jev budget
                  </Button>
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                icon={Download}
                disabled={exporter.isPreparing}
                title="Everyone in this job, not only what the filters show: one sheet for each tab"
                onClick={() => exporter.run()}
              >
                {exporter.isPreparing ? 'Preparing…' : 'Export to Excel'}
              </Button>
            </>
          }
          jevOrder={isJevOrder && settledJev ? { ask: settledJev.ask.text, onRemove: () => update({ rask: undefined, rorder: undefined }) } : null}
        />

        <div className={styles.filterTools}>
          <RankingFilterBar
            rows={filterBase}
            filters={filters}
            keys={filterKeys}
            onChange={(next) => update(searchPatchOf(next))}
            extra={areLineFiltersOffered ? <LineFilterButtons lines={lineItems} jobRatings={icp.baselineRatings} filters={lineFilters} onChange={(next) => update({ rline: lineFilterValueOf(next, icp.items) })} /> : undefined}
          />
          <div className={styles.tools}>
            <div className={styles.search}>
              {/* With Jev on, `/` goes to the Ask Jev box instead (ADR 0050, as in the mockup). */}
              <SearchInput data-primary-search={isJevPane ? undefined : true} placeholder="Search names" value={search.q ?? ''} onChange={(event) => update({ q: event.target.value || undefined })} onClear={() => update({ q: undefined })} aria-label="Search names" />
            </div>
            {/* Owner 2026-11-01 Q9: the keywords open from here, so the table on ICP match is in view. */}
            {!isJevPane && <KeywordsButton jobId={detail.id} keywords={keywords} onChange={setKeywords} matchCount={(word) => matchSets.get(word.toLowerCase())?.size ?? null} note={keywordNote} />}
            {!isJevPane && <Segmented label="Order" value={orderValue} options={orderOptions} onChange={(value) => update({ rorder: orderPatchOf(value, isProfileRanked) })} />}
            <ModuleGate module="screening_criteria">
              {!isJevPane && view.criteria.length > 1 && (
                <div className={styles.focus}>
                  <MultiSelect
                    id={focusId}
                    label="Focus on"
                    placeholder="Focus on…"
                    options={view.criteria.map((criterion) => ({ value: criterion.key, label: criterion.label, description: CRITERION_IMPORTANCE_LABELS[criterion.importance] }))}
                    value={focusKeys}
                    // Focusing a criterion is the criteria order's: the list goes there, and back to the profile when the focus is cleared.
                    onChange={(keys) => update({ focus: keys.length > 0 ? keys.join(',') : undefined, rorder: keys.length > 0 && isProfileRanked ? 'score' : undefined })}
                  />
                </div>
              )}
            </ModuleGate>
            <Segmented label="Layout" value={layout} options={LAYOUT_OPTIONS} onChange={(value) => update({ rlayout: value === 'list' ? 'list' : undefined })} />
          </div>
      </div>

      <div className={styles.status} aria-live="polite">
        {view.isReranking ? (
          <ProgressLine label="Re-ranking… the order below is the last one until it's done" />
        ) : pending > 0 && isOldOrder ? (
          <ProgressLine label={`${ranked.toLocaleString('en-US')} of ${view.rows.length.toLocaleString('en-US')} ranked`} share={ranked / view.rows.length} />
        ) : null}
      </div>

      <div className={styles.tableBox}>
        {/* DECISIONS-LOG 2026-10-26: the numbers sit on top of the table's own box, so the order can be watched while one changes. */}
        {!isJevPane && isProfileOrder && icp.hasProfile && (
          <IcpFocusBar items={icp.items} isAtBaseline={icp.isAtBaseline} canEdit={canEditProfile} isEditing={icp.panel === 'form'} flash={icp.flash} onRate={icp.rate} onReset={icp.reset} />
        )}
        <div className={styles.tableHead}>
          {isOldOrder ? <span className={styles.oldOrderPill}>Old order · score</span> : <RankTabs value={tab} counts={counts} onChange={(value) => update({ tab: value })} />}
          <span className={styles.tableHeadNote}>{headNoteOf({ isOldOrder, isJevOrder, isCriteriaOrder, isFocusing: focusKeys.length > 0, isWaitingForAsk: isJevPane && !isJevOrder })}</span>
        </div>
        {!isJevPane && isProfileOrder && icp.reading && <JevReadingLine done={icp.reading.done} total={icp.reading.total} />}
        {isJevReading && <JevReadingLine done={latestAsk?.ask.status === 'running' ? latestAsk.ask.done : 0} total={latestAsk?.ask.status === 'running' ? latestAsk.ask.total : view.rows.length} />}
        <div className={styles.tableScroll}>
        <DataTable
          screenId="ranking"
          label={`${RANKING_PANE_LABELS[pane]}: ${RANK_TAB_LABELS[tab]}`}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.applicationId}
          sort={[{ columnId: sort.by, direction: sort.direction }]}
          onSortChange={(next) => update({ rsort: rsortOf(next) })}
          selection={selection}
          onSelectionChange={setSelection}
          isSelectable={canDecide}
          onOpenRow={(row) => update({ app: row.applicationId })}
          onRowKey={onRowKey}
          expandedRowId={remarkRowId}
          renderExpanded={(row) => (
            <InlineRemark
              key={row.applicationId}
              row={row}
              onCancel={() => setRemarkRowId(null)}
              onSave={(draft) => {
                setRemarkRowId(null);
                void remarkActions.add(row.applicationId, draft);
              }}
            />
          )}
          bulkActions={
            <>
              <Button
                size="sm"
                variant="outline"
                icon={ListChecks}
                onClick={() => {
                  const items = selected.filter(isWaitingForReview).map((row) => ({ applicationId: row.applicationId, name: row.name, from: row.reviewStatus ?? 'to_review' }));
                  setSelection(EMPTY_SELECTION);
                  void move(items, 'shortlisted');
                }}
              >
                Shortlist
              </Button>
              <Button size="sm" variant="danger" icon={X} onClick={(event) => setRejecting({ rows: selected.filter((row) => row.stage === 'screening'), anchor: anchorFrom(event.currentTarget) })}>
                Reject…
              </Button>
              {canRemark && (
                <Button size="sm" variant="outline" icon={MessageSquare} onClick={(event) => setRemarking({ rows: selected, anchor: anchorFrom(event.currentTarget) })}>
                  Add a remark
                </Button>
              )}
              <AddToCvBankButton people={selected.map((row) => ({ candidateId: row.candidateId, name: row.name }))} onDone={() => setSelection(EMPTY_SELECTION)} />
              <Button size="sm" variant="outline" icon={Download} disabled={exporter.isPreparing} onClick={() => exporter.run(selected.map((row) => row.applicationId))}>
                Export selected to Excel
              </Button>
            </>
          }
          emptyState={
            lineHelp ? (
              <LineFilterEmpty
                help={lineHelp}
                onSetRating={(id, rating) => update({ rline: lineFilterValueOf({ ...lineFilters, [id]: rating }, icp.items) })}
                onTurnOff={(id) => update({ rline: lineFilterValueOf(withoutLine(id), icp.items) })}
                onClearAll={clearAllFilters}
              />
            ) : (
            <EmptyState
              title={
                search.q
                  ? `Nobody in ${RANK_TAB_LABELS[tab]} matches "${search.q}"`
                  : isFiltering
                    ? `Nobody in ${RANK_TAB_LABELS[tab]} matches ${activeRankingFilters(filters).length === 1 ? 'this filter' : 'these filters'}`
                    : isNarrowing
                      ? `Nobody in ${RANK_TAB_LABELS[tab]} has ${keywords.length === 1 ? `"${keywords[0] ?? ''}"` : 'all of these words'} in their CV`
                      : `Nobody in ${RANK_TAB_LABELS[tab]}`
              }
              body={
                otherMatches.length > 0
                  ? `Found in ${otherMatches.map((match) => `${RANK_TAB_LABELS[match.tab]} (${match.count})`).join(', ')}.`
                  : next
                    ? `${RANK_TAB_LABELS[next]} has ${counts[next].toLocaleString('en-US')}.`
                    : 'Nobody is ranked yet.'
              }
              action={
                otherMatches.length > 0 ? (
                  <span className={styles.switchTabs}>
                    {otherMatches.map((match) => (
                      <Button key={match.tab} variant="outline" onClick={() => update({ tab: match.tab })}>
                        Open {RANK_TAB_LABELS[match.tab]}
                      </Button>
                    ))}
                  </span>
                ) : next ? (
                  <Button variant="outline" onClick={() => update({ tab: next })}>
                    Open {RANK_TAB_LABELS[next]}
                  </Button>
                ) : (
                  <Button variant="outline" onClick={() => void navigate({ to: '/jobs/$jobId/$tab', params: { jobId: detail.id, tab: 'import' } })}>
                    Import a CSV
                  </Button>
                )
              }
              isInset
            />
            )
          }
        />
        </div>
        <KeyHints canDecide={canDecide} canRemark={canRemark} isJevOn={isJevPane} canEditProfile={canEditProfile} isCriteriaOn={isCriteriaOn && view.criteria.length > 1} />
      </div>

      <WhyThisScore
        applicationId={search.app ?? null}
        jobId={detail.id}
        onClose={() => update({ app: undefined })}
        review={canDecide && openedRow ? { row: openedRow, isMoving: movingIds.has(openedRow.applicationId), onShortlist: () => shortlistOne(openedRow) } : undefined}
        jev={settledJev && openedRow ? { askId: settledJev.ask.id, parts: settledJev.ask.parts, result: jevResults.get(openedRow.applicationId), place: isJevOrder ? openedPlace : null, isPractice: settledJev.ask.provider === 'practice' } : undefined}
        icp={isProfileRanked && openedRow ? sheetProfileOf(openedRow, icp, isProfileOrder ? openedPlace : null) : undefined}
        steps={steps}
      />

      <Popover anchor={remarking?.anchor ?? null} onClose={() => setRemarking(null)} label="Remark" align="left">
        {remarking && (
          <RemarkPopover
            key={remarking.rows.map((row) => row.applicationId).join(',')}
            rows={remarking.rows}
            onCancel={() => setRemarking(null)}
            onSave={(draft) => {
              const targets = remarking.rows;
              setRemarking(null);
              const [only] = targets;
              if (targets.length === 1 && only) {
                void remarkActions.add(only.applicationId, draft);
                return;
              }
              setSelection(EMPTY_SELECTION);
              void remarkActions.addMany(
                targets.map((row) => row.applicationId),
                { tags: draft.tags, body: draft.body, visibility: draft.visibility },
              );
            }}
          />
        )}
      </Popover>

      <Popover anchor={rejecting?.anchor ?? null} onClose={() => setRejecting(null)} label="Reject" align="left">
        {rejecting && (
          <ReasonPicker
            key={rejecting.rows.map((row) => row.applicationId).join(',')}
            mode="reject"
            reasons={reasons}
            pools={options.data?.pools ?? []}
            canPool={isPoolsOn && mayPool}
            contactAttempts={fewestContactAttempts(rejecting.rows)}
            candidateName={rejecting.rows.length === 1 && rejecting.rows[0] ? rejecting.rows[0].name : `${rejecting.rows.length} people`}
            canAddToCvBank={rejecting.rows.some((row) => row.cv !== 'none')}
            onConfirm={(choice) => {
              const people = rejecting.rows.map((row) => ({ applicationId: row.applicationId, name: row.name }));
              setRejecting(null);
              setSelection(EMPTY_SELECTION);
              void rejection.reject(people, choice.reasonCode, { poolIds: choice.poolIds, newPoolNames: choice.newPoolNames, addToCvBank: choice.addToCvBank });
            }}
          />
        )}
      </Popover>
      </div>
    </div>
  );
}

/** One row's remark box, inside the list under the row; it fetches who this job lets you mention. */
function InlineRemark({ row, onSave, onCancel }: { readonly row: RankingRow; readonly onSave: (draft: RemarkDraft) => void; readonly onCancel: () => void }): ReactNode {
  const remarks = useApplicationRemarks(row.applicationId);
  return <RemarkComposer heading={`Remark on ${row.name}`} mentionable={remarks.data?.mentionable ?? []} onSubmit={onSave} onCancel={onCancel} autoFocus />;
}

/** The remark box over one row (with who can be mentioned) or over the selected rows (no mentions). */
function RemarkPopover({ rows, onSave, onCancel }: { readonly rows: readonly RankingRow[]; readonly onSave: (draft: RemarkDraft) => void; readonly onCancel: () => void }): ReactNode {
  const [only] = rows;
  const single = rows.length === 1 ? only : undefined;
  const remarks = useApplicationRemarks(single?.applicationId ?? null);
  return (
    <div className={remarkStyles.popover}>
      <RemarkComposer
        heading={single ? `Remark on ${single.name}` : `One remark on each of ${rows.length} people`}
        mentionable={single ? (remarks.data?.mentionable ?? []) : []}
        onSubmit={onSave}
        onCancel={onCancel}
        autoFocus
      />
    </div>
  );
}

function uniqueWords(words: readonly string[]): readonly string[] {
  const seen = new Set<string>();
  return words.filter((word) => {
    const key = word.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function matchSetsOf(words: readonly string[], matches: readonly (readonly string[] | undefined)[]): ReadonlyMap<string, ReadonlySet<string>> {
  const sets = new Map<string, ReadonlySet<string>>();
  words.forEach((word, index) => {
    const ids = matches[index];
    if (ids) sets.set(word.toLowerCase(), new Set(ids));
  });
  return sets;
}

/** What a view would keep, in words, for the Save view pop-over (F23.10). */
function viewSummary(criteria: readonly RankingCriterionView[], tab: RankTab, keywords: readonly string[], search: PipelineSearch, filters: RankingFilters, jevAsk: string | null, isCriteriaLens: boolean, lineFilters: readonly string[]): readonly string[] {
  const lines = [`Tab: ${RANK_TAB_LABELS[tab]}`];
  if (search.rask && jevAsk) lines.push(`Jev ask: "${jevAsk}"${search.rorder === 'jev' ? ', in Jev’s order' : ''}`);
  if (isCriteriaLens) lines.push('Order: screening criteria score');
  if (keywords.length > 0) lines.push(`Keywords: ${keywords.join(', ')}`);
  for (const item of activeRankingFilters(filters)) lines.push(`${RANKING_FILTER_LABELS[item.key]}: ${item.label}`);
  lines.push(...lineFilters);
  const focus = search.focus ? search.focus.split(',').map((key) => criteria.find((criterion) => criterion.key === key)?.label).filter((label): label is string => label !== undefined) : [];
  if (focus.length > 0) lines.push(`Focus on: ${focus.join(', ')}`);
  if (search.q) lines.push(`Name search: "${search.q}"`);
  return lines;
}

/** Still waiting in HR's review: in `screening`, To review or Maybe (§L31). */
function RankTabs({ value, counts, onChange }: { readonly value: RankTab; readonly counts: Readonly<Record<RankTab, number>>; readonly onChange: (tab: RankTab) => void }): ReactNode {
  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = RANK_TABS[(index + step + RANK_TABS.length) % RANK_TABS.length];
    if (next) onChange(next);
  }
  return (
    <div className={styles.tabs} role="tablist" aria-label="Ranking tabs">
      {RANK_TABS.map((tab, index) => (
        <button
          key={tab}
          type="button"
          role="tab"
          aria-selected={tab === value}
          tabIndex={tab === value ? 0 : -1}
          className={styles.tab}
          onClick={() => onChange(tab)}
          onKeyDown={(event) => onKeyDown(event, index)}
        >
          {tab === 'needs_a_look' && <span className={styles.tabDot} aria-hidden />}
          {RANK_TAB_LABELS[tab]}
          <span className={styles.tabCount}>{counts[tab].toLocaleString('en-US')}</span>
        </button>
      ))}
    </div>
  );
}

function ProgressLine({ label, share }: { readonly label: string; readonly share?: number }): ReactNode {
  return (
    <div className={styles.progress}>
      <span className={styles.progressLabel}>{label}</span>
      <span className={styles.progressTrack} data-indeterminate={share === undefined || undefined}>
        <span className={styles.progressFill} style={share === undefined ? undefined : { inlineSize: `${Math.round(share * 100)}%` }} />
      </span>
    </div>
  );
}

interface ShortlistAction {
  readonly shortlist: (row: RankingRow) => void;
  readonly moving: ReadonlySet<string>;
}

interface RemarkAction {
  readonly canAdd: boolean;
  readonly pending: ReadonlySet<string>;
  readonly open: (row: RankingRow) => void;
}

/** ADR 0050: the ask whose answers the Jev match column shows. */
interface JevColumn {
  readonly parts: readonly JevAskPart[];
  readonly results: ReadonlyMap<string, JevPersonResult>;
}

/** ADR 0062: the profile, everyone's rank and who moved — what the profile order adds after the match. */
interface ProfileColumns {
  readonly items: readonly IcpItem[];
  readonly ranks: ReadonlyMap<string, IcpRanked>;
  readonly moved: ReadonlyMap<string, number | null>;
  /** ADR 0070 stage 2: the job's Jev budget is used up, so people not yet read are "Waiting for budget". */
  readonly isBudgetUsedUp?: boolean;
}

interface ColumnsInput {
  readonly jobId: string;
  readonly criteria: readonly RankingCriterionView[];
  readonly layout: Layout;
  readonly isFocusing: boolean;
  readonly open: (applicationId: string) => void;
  readonly action: ShortlistAction | null;
  readonly isNarrowing: boolean;
  readonly remark: RemarkAction;
  readonly jev: JevColumn | null;
  /** The profile is the job's main ranking: Match, Per item and Moved. Null for a job still in its old order (Score and Criteria). */
  readonly profile: ProfileColumns | null;
  /** Screening criteria is on: their ✓◐✗ columns show, as the lens beside the profile or as the old order's own columns. */
  readonly isCriteriaOn: boolean;
}

export function columnsOf({ jobId, criteria, layout, isFocusing, open, action, isNarrowing, remark, jev, profile, isCriteriaOn }: ColumnsInput): readonly Column<RankedListRow>[] {
  const lead: Column<RankedListRow>[] = [
    { id: 'rank', header: '#', cell: (row) => row.rank, isNumeric: true, defaultWidth: 56 },
    {
      id: 'name',
      header: 'Name',
      isIdentity: true,
      defaultWidth: 300,
      exportValue: (row) => row.name,
      // D15: a click anywhere in the cell opens the sheet. The name stays a real button for the
      // keyboard; its click (or Enter) bubbles to this one handler, so the sheet opens once.
      cell: (row) => (
        <span className={`${styles.nameCell} ${styles.cellOpener}`} role="presentation" onClick={() => open(row.applicationId)}>
          <button type="button" className={styles.nameButton}>
            {row.name}
          </button>
          {row.missingMustHaves.length > 0 && <Chip tone="rejected">Missing: {row.missingMustHaves.join(', ')}</Chip>}
          {isNarrowing && isUncheckable(row) && <Chip tone="attention">Not checked for keywords</Chip>}
          {profile && <CandidateSummary facts={row} />}
        </span>
      ),
    },
    // ADR 0062, as in mockup M2: the profile's Match — level, number, stars and "fit · covers" — in one clickable cell; a job still in
    // its old order keeps the score over its stars (ADR 0049).
    profile
      ? {
          id: SCORE_SORT,
          header: 'Match',
          isSortable: true,
          defaultWidth: 260,
          exportValue: (row) => row.score,
          cell: (row) => (
            <button type="button" className={styles.scoreButton} onClick={() => open(row.applicationId)} aria-label={`Why ${row.name} is placed here, match ${row.score ?? 'not ranked yet'}`}>
              <MatchCell rank={profile.ranks.get(row.applicationId)} cv={row.cv} {...(profile.isBudgetUsedUp ? { waitingLabel: 'Waiting for budget' } : {})} />
            </button>
          ),
        }
      : {
          id: SCORE_SORT,
          header: isFocusing ? 'Focus' : 'Score',
          isSortable: true,
          defaultWidth: 112,
          exportValue: (row) => row.focus ?? row.score,
          cell: (row) => (
            <button type="button" className={styles.scoreButton} onClick={() => open(row.applicationId)} aria-label={`Why ${row.name} scored ${row.score ?? 'nothing yet'}`}>
              <span className={styles.scoreNumber}>
                {isFocusing ? (row.focus ?? '—') : (row.score ?? '—')}
                {isFocusing && <span className={styles.focusTag}>focus</span>}
              </span>
              <Stars value={row.stars} />
            </button>
          ),
        },
    // ADR 0050/0051, as in the mockup: Jev's match beside the score, a grade per part and Haiku's one-line reason.
    ...(jev
      ? [
          {
            id: 'jev',
            header: 'Jev match · why',
            defaultWidth: 280,
            exportValue: (row: RankedListRow) => jev.results.get(row.applicationId)?.match ?? null,
            cell: (row: RankedListRow) => <JevCell result={jev.results.get(row.applicationId)} parts={jev.parts} />,
          },
        ]
      : []),
    // ADR 0061/0062, as in the mockup: a box per line (always on, replacing the criteria grid), and who moved.
    ...(profile
      ? [
          {
            id: 'icp-items',
            header: perItemHeaderOf(profile.items),
            defaultWidth: 440,
            cell: (row: RankedListRow) => {
              const ranked = profile.ranks.get(row.applicationId);
              return ranked ? <ItemGlyphs items={profile.items} values={ranked.values} /> : null;
            },
          },
          // Once Jev has read someone: its findings per line, opened beside the cell (the sheet's "What Jev found" on its own).
          ...(isJevRead(profile.ranks)
            ? [
                {
                  id: 'jev-found',
                  header: 'What Jev found',
                  defaultWidth: 280,
                  exportValue: (row: RankedListRow) => {
                    const ranked = profile.ranks.get(row.applicationId);
                    const best = ranked?.state === 'read' ? jevFoundOf(profile.items, ranked.values).best : null;
                    return best ? `${best.item.text}: ${best.glyph.label}` : null;
                  },
                  cell: (row: RankedListRow) => <JevFoundCell jobId={jobId} row={row} items={profile.items} ranked={profile.ranks.get(row.applicationId)} />,
                },
              ]
            : []),
          {
            id: 'icp-moved',
            header: 'Moved',
            defaultWidth: 96,
            exportValue: (row: RankedListRow) => profile.moved.get(row.applicationId) ?? null,
            cell: (row: RankedListRow) => <MovedCell moved={profile.moved.get(row.applicationId)} />,
          },
        ]
      : []),
  // §L31: HR's own stars, beside the ATS score and never part of it; `—` until rated (RNK-01 §6).
    { id: 'hr', header: 'HR ★', isNumeric: true, defaultWidth: 80, cell: (row) => (row.hrStars === null ? '—' : row.hrStars.toFixed(1)), exportValue: (row) => row.hrStars },
  ];
  const criteriaSummary: Column<RankedListRow> = {
    id: 'criteria-summary',
    header: 'Criteria',
    defaultWidth: 160,
    exportValue: (row) => row.cells.filter((cell) => cell.mark === 'met').length,
    cell: (row) => <span className={styles.muted}>Criteria met: {row.cells.filter((cell) => cell.mark === 'met').length} of {criteria.length}</span>,
  };
  // Screening criteria is a switchable module: with it off its columns leave no trace, and a job still in its old order shows one summary instead.
  const marks: Column<RankedListRow>[] = !isCriteriaOn
    ? profile
      ? []
      : [criteriaSummary]
    : layout === 'grid'
      ? criteria.map((criterion, index) => ({
          id: criterion.key,
          header: criterion.label,
          headerContent: <CriterionHeader criterion={criterion} />,
          headerTitle: criterionTitle(criterion),
          isSortable: true,
          defaultWidth: 112,
          exportValue: (row: RankedListRow) => row.cells[index]?.mark ?? null,
          cell: (row: RankedListRow) => {
            const cell = row.cells[index];
            return cell ? <MarkGlyph mark={cell.mark} isCorrected={cell.isCorrected} label={criterion.label} isTile /> : null;
          },
        }))
      : [{ id: 'coverage', header: 'Read', isNumeric: true, defaultWidth: 88, exportValue: (row: RankedListRow) => row.coverage, cell: (row: RankedListRow) => (row.coverage === null ? null : <CoverageChip value={row.coverage} />) }];
  const review: Column<RankedListRow> = {
    id: 'review',
    header: 'Shortlist',
    defaultWidth: 128,
    exportValue: (row) => (row.reviewStatus === 'shortlisted' ? 'Shortlisted' : ''),
    cell: (row) => {
      if (row.reviewStatus === 'shortlisted') return <Chip tone="success">Shortlisted</Chip>;
      if (!action || !isWaitingForReview(row)) return <span className={styles.muted}>—</span>;
      return (
        <Button
          size="sm"
          variant="outline"
          icon={ListChecks}
          disabled={action.moving.has(row.applicationId)}
          aria-label={`Shortlist ${row.name}`}
          onClick={(event) => {
            event.stopPropagation();
            action.shortlist(row);
          }}
        >
          Shortlist
        </Button>
      );
    },
  };
  // F23.13: the latest remark the viewer may see, or "Remark" to add one; M on the focused row does the same.
  const remarks: Column<RankedListRow> = {
    id: 'remark',
    header: 'Remark',
    defaultWidth: 220,
    exportValue: (row) => (row.remarks.latest ? remarkLine(row.remarks.latest) : ''),
    cell: (row) => <RemarkCell summary={row.remarks} name={row.name} isPending={remark.pending.has(row.applicationId)} canAdd={remark.canAdd} onOpen={() => remark.open(row)} />,
  };
  // D15: the CV cell opens the same sheet, whose right side is the CV.
  const cv: Column<RankedListRow> = {
    id: 'cv',
    header: 'CV',
    defaultWidth: 140,
    exportValue: (row) => row.cv,
    cell: (row) => (
      <button type="button" className={`${styles.cvButton} ${styles.cellOpener}`} onClick={() => open(row.applicationId)} aria-label={`Open the CV of ${row.name}`}>
        <CvState state={row.cv} kind={row.cvKind} />
      </button>
    ),
  };
  return [...lead, ...marks, remarks, cv, review];
}

/** §L19: GEN-UNRESPONSIVE needs 3 logged attempts, so a reject of many allows it only when everyone has them. */
function fewestContactAttempts(rows: readonly RankingRow[]): number {
  return rows.length === 0 ? 0 : Math.min(...rows.map((row) => row.contactAttempts));
}

/** Jev has read at least one CV for this job, so there is something to show in "What Jev found". */
function isJevRead(ranks: ReadonlyMap<string, IcpRanked>): boolean {
  return [...ranks.values()].some((ranked) => ranked.state === 'read');
}

/** A criterion's column header: a dot for its importance, then its name; the tooltip says both in words. */
function CriterionHeader({ criterion }: { readonly criterion: RankingCriterionView }): ReactNode {
  return (
    <span className={styles.columnHead}>
      <span className={styles.importanceDot} data-importance={criterion.importance} aria-hidden />
      {criterion.label}
    </span>
  );
}

/** §L30 coverage, "Read 44% of what you asked for"; under half takes the progress tone. */
export function CoverageChip({ value }: { readonly value: number }): ReactNode {
  return (
    <Chip tone={value < 50 ? 'progress' : 'ended'}>
      <span title={`Read ${value}% of what you asked for`}>{value}%</span>
    </Chip>
  );
}

/** The CV's file type and whether its text was read (ADR 0049, as in the mockup). */
function CvState({ state, kind }: { readonly state: RankedListRow['cv']; readonly kind: RankedListRow['cvKind'] }): ReactNode {
  if (state === 'none') {
    return (
      <span className={styles.cvCell}>
        <span className={styles.cvKind}>
          <FileX size={14} aria-hidden /> No CV
        </span>
        <span className={styles.cvSub} data-warn>
          Form answers only
        </span>
      </span>
    );
  }
  const Icon = kind === 'pdf' || kind === 'word' ? FileText : FileIcon;
  const label = kind === 'pdf' ? 'PDF' : kind === 'word' ? 'Word' : 'Other file';
  const sub = state === 'ready' ? 'Text read' : state === 'coming' ? 'CV coming' : "Couldn't be read";
  return (
    <span className={styles.cvCell}>
      <span className={styles.cvKind}>
        <Icon size={14} aria-hidden /> {label}
      </span>
      <span className={styles.cvSub} data-warn={state === 'unreadable' || undefined}>
        {sub}
      </span>
    </span>
  );
}

interface NoProfileProps {
  readonly detail: JobDetail;
  readonly rows: readonly RankingRow[];
  readonly canEdit: boolean;
  /** A job with no profile that is still in its old order, with the module on: the old prompt to add criteria. */
  readonly isCriteriaPromptShown: boolean;
  readonly onOpenRow: (applicationId: string) => void;
}

/** Mockup M2 "No profile yet": say so, point at job setup, and keep everyone listed in the order they came in with nothing ranked. */
function NoProfile({ detail, rows, canEdit, isCriteriaPromptShown, onOpenRow }: NoProfileProps): ReactNode {
  const navigate = useNavigate();
  const listed = useMemo(() => [...rows].sort((a, b) => a.order - b.order).map((row, index) => ({ ...row, rank: index + 1, focus: null })), [rows]);
  const columns = useMemo<readonly Column<RankedListRow>[]>(
    () => [
      { id: 'rank', header: '#', cell: (row) => row.rank, isNumeric: true, defaultWidth: 56 },
      {
        id: 'name',
        header: 'Name',
        isIdentity: true,
        defaultWidth: 300,
        exportValue: (row) => row.name,
        cell: (row) => (
          <span className={`${styles.nameCell} ${styles.cellOpener}`} role="presentation" onClick={() => onOpenRow(row.applicationId)}>
            <button type="button" className={styles.nameButton}>
              {row.name}
            </button>
          </span>
        ),
      },
      { id: 'match', header: 'Match', defaultWidth: 120, cell: () => <span className={styles.muted}>—</span> },
      { id: 'items', header: 'Per item', defaultWidth: 120, cell: () => <span className={styles.muted}>—</span> },
    ],
    [onOpenRow],
  );
  const toSetup = (section: string) => (): void => void navigate({ to: '/jobs/$jobId/setup/$section', params: { jobId: detail.id, section } });
  const title = isCriteriaPromptShown ? 'Nothing to rank on yet' : 'No ideal candidate yet';
  const body = isCriteriaPromptShown
    ? 'Add screening criteria to the job, and every candidate is ranked against them.'
    : 'Automated ranking needs an ideal candidate to rank against. Until then everyone is listed in the order they came in, and Jev can’t read their CVs.';
  return (
    <div className={styles.stage}>
      <EmptyState
        title={title}
        body={body}
        action={
          canEdit || isCriteriaPromptShown ? (
            <Button variant="solid" onClick={toSetup(isCriteriaPromptShown ? 'criteria' : 'profile')}>
              {isCriteriaPromptShown ? 'Add screening criteria' : 'Set it in job setup'}
            </Button>
          ) : undefined
        }
        isInset
      />
      {listed.length > 0 && (
        <div className={styles.tableBox}>
          <div className={styles.tableScroll}>
            <DataTable screenId="ranking-unranked" label="Candidates, not ranked yet" columns={columns} rows={listed} getRowId={(row) => row.applicationId} sort={NO_SORT} onSortChange={noop} selection={EMPTY_SELECTION} onSelectionChange={noop} onOpenRow={(row) => onOpenRow(row.applicationId)} />
          </div>
        </div>
      )}
    </div>
  );
}

function RankingSkeleton(): ReactNode {
  return (
    <div className={styles.stage} aria-busy="true">
      <Skeleton shape="title" width="40%" />
      <Skeleton shape="card" />
    </div>
  );
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.closest('[role="dialog"], [role="listbox"]') !== null;
}

function sortOf(value: string | undefined): RankingSort {
  if (!value) return DEFAULT_SORT;
  const [by, direction] = value.split(':');
  return by && (direction === 'asc' || direction === 'desc') ? { by, direction } : DEFAULT_SORT;
}

function rsortOf(next: readonly SortSpec[]): string | undefined {
  const first = next[0];
  if (!first || (first.columnId === SCORE_SORT && first.direction === 'desc')) return undefined;
  return `${first.columnId}:${first.direction}`;
}

/** The ranking's part of the address, everything else kept. `undefined` clears a key. */
function withRanking(search: PipelineSearch, patch: Partial<PipelineSearch>): PipelineSearch {
  // Through the address's own reader, so a saved view's strings are checked like a typed link.
  return validatePipelineSearch({ ...search, ...patch });
}

